import { type UseTransitionProps, a, useTransition } from "@react-spring/web";
/** @format */
import React, { type FC, useEffect, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { delay, handleProcessFilters } from "senf-shared";
import i18n from "senf-shared/utils/i18n";
import styled, { css } from "styled-components";
import TopicTag from "../../atoms/tag/TopicTag";
import type { TagSlideProps } from "./TagSlide.types";

interface TopicTagProps {
  name: string;
  onClick?: () => void;
  active?: boolean;
  icon?: JSX.Element;
  text: string | JSX.Element;
  type: TagSlideProps["type"];
}
const Outer = styled.div<{ direction: TagSlideProps["direction"] }>`
  --padding: 17px;
  --width: 100%;
  --height: calc(var(--padding) * 2 + 36px);
  --overflow: scroll hidden;
  --flex-direction: row;
  padding: var(--padding);

  overflow: var(--overflow);
  width: var(--width);
  height: var(--height);
  ${({ direction }) =>
    direction === "vertical" &&
    css`
      --padding: 0;
      --width: initial;
      --height: initial;
      --overflow: visible;
      --flex-direction: column;
    `}
  position: relative;
  pointer-events: all;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    padding: 10px;
    padding-left: ${({ direction }) =>
      direction === "horizontal" ? "20px" : undefined};
  }
`;
const Wrapper = styled.section`
  height: auto;
  box-sizing: border-box;
  gap: 8px;
  z-index: 99999999;
  display: flex;
  flex-direction: var(--flex-direction);
  pointer-events: none;
  > * {
    &:last-child {
      margin-right: var(--padding);
    }
    button {
      pointer-events: auto;
    }
  }
`;
const TagSlide: FC<TagSlideProps> = ({
  arealFilters,
  type,
  hide,
  tags,
  activeFilters,
  setActiveFilters,
  selectedArealFilters,
  setSelectedArealFilter,
  direction = "horizontal",
  className,
  maxWidth,
  handle,
}) => {
  const { t } = useTranslation();

  const matchingFilters = activeFilters?.find(
    ({ filterName }) => filterName === type,
  )?.selectedFilters;

  useEffect(() => {
    if (
      matchingFilters?.length === tags?.length &&
      selectedArealFilters === null
    ) {
      // setActiveTopics([]);
      setSelectedArealFilter?.(null);
      handleProcessFilters(setActiveFilters, type, []);
    }
    // setSelectedTag(selectedTag);
    // setSelectedTag(type, matchingFilters);
  }, [tags?.length, activeFilters]);

  const filterTags: TopicTagProps[] = useMemo(
    () =>
      [
        {
          name: "all",
          onClick: () => {
            handle?.clickTag?.();
            handleProcessFilters(setActiveFilters, type, []);
            setSelectedArealFilter?.(null);
          },
          active:
            !matchingFilters?.length &&
            (!setSelectedArealFilter || selectedArealFilters === null),
          icon: "ideaCategory/all",
          text: t("global:all"),
          type,
        } as const,
        ...(tags?.map(
          (item) =>
            ({
              name: item.name,
              onClick: () => {
                handle?.clickTag?.();
                handleProcessFilters(setActiveFilters, type, item.value);
              },
              active: matchingFilters?.includes(item.value),
              icon: "ideaCategory/default",
              iconColor: item.color,
              text: item.label,
              type,
            }) as const,
        ) || []),
        ...(arealFilters?.map(
          (item) =>
            ({
              name: item.name,
              onClick: () => {
                handle?.clickTag?.();
                setSelectedArealFilter?.(
                  selectedArealFilters === item.value ? null : item.value,
                );
              },
              active: selectedArealFilters === item.value,
              icon: item.leadingIcon,
              text: item.label,
              type: "arealFilters",
            }) as const,
        ) || []),
      ].filter(Boolean),

    [arealFilters, activeFilters, selectedArealFilters, i18n?.resolvedLanguage],
  );

  const props = (items: number[]): [number[], UseTransitionProps] => [
    items,
    {
      from: { scale: 1, x: 10, opacity: 0 },
      enter: () => async (next) => {
        await delay(hide ? 0 : 300);

        await next({
          scale: 1,
          opacity: 1,
          x: 0,
        });
      },
      leave: { scale: 0.9, opacity: 0 },
      config: {
        tension: 200,
        friction: 15,
      },
      trail: 40,
    },
  ];
  const filterTansitions = useTransition(
    ...props(!hide && filterTags?.length ? filterTags.map((e, i) => i) : []),
  );

  return (
    <Outer direction={direction} className="hide-scrollbar">
      <Wrapper
        className={className}
        aria-label={t("global:filters")}

        // role="region"
        // aria-label="Filter Optionen"
      >
        {filterTansitions((style, index) => (
          <a.div style={style} key={index}>
            <TopicTag
              {...filterTags[index]}
              item={filterTags[index]}
              maxWidth={maxWidth}
            />
          </a.div>
        ))}
      </Wrapper>
    </Outer>
  );
};

export default TagSlide;
